/* eslint-disable sort-keys */
import { Box, Button, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import React from "react";
import { useTranslation } from "react-i18next";
import approvedIcon from "../../assets/Approvata.svg";
import pendingIcon from "../../assets/In attesa.svg";
import rejectedIcon from "../../assets/Negata.svg";
import { CommonWidgetContainer } from "../../components/Commons/CommonWidgetContainer.styles";
import { Icon } from "../Icon/Icon.component";

// interface CourseItem {
//   title: string;
//   type?: string;
//   provider: string;
//   startDate?: string; // si presume come ISO string (es. "2025-05-02")
//   endDate?: string;
//   status: "APPROVED" | "REJECTED" | "PENDING";
// }

interface CoursesTableProps {
  courses: CourseItem[];
  selectedCourse: CourseItem | null;
  onSelect: (course: CourseItem) => void;
  filter: string;
  paginatedCourses: CourseItem[];
  currentPage: number;
  itemsPerPage: number;
  setCurrentPage: (page: number) => void;
}

export const CoursesTable: React.FC<CoursesTableProps> = ({
  courses,
  onSelect,
  selectedCourse,
  filter,
  paginatedCourses,
  currentPage,
  itemsPerPage,
  setCurrentPage
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));

  // Funzione di utilità per formattare la data in italiano o restituire "-"
  const formatDateIt = (dateString?: string) => {
    return dateString
      ? new Date(dateString).toLocaleDateString("it-IT", {
        day: "2-digit",
        month: "short",
        year: "numeric"
      })
      : "-";
  };

  const getPaginationItems = (current: number, total: number, maxVisible = 5) => {
    const items = [];
  
    if (total <= maxVisible) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }
  
    const left = Math.max(1, current - 1);
    const right = Math.min(total, current + 1);
  
    if (left > 2) items.push(1, "...");
    else for (let i = 1; i < left; i++) items.push(i);
  
    for (let i = left; i <= right; i++) items.push(i);
  
    if (right < total - 1) items.push("...", total);
    else for (let i = right + 1; i <= total; i++) items.push(i);
  
    return items;
  };
  

  return (
    <Box
      sx={ {
        display: "flex",
        flexDirection: "column",
        flexGrow: 1,
        mt: 4,
        minWidth: 0
      } }
    >
      { /* Header delle colonne: mostrato solo su schermi > xs */ }
      { !isXs && (
        <Box
          sx={ {
            borderBottom: `1px solid ${theme.customColors.border}`,
            display: "grid",
            fontWeight: "bold",
            gridTemplateColumns:
              filter === "all"
                ? "2fr 1.5fr 1.5fr 1.5fr 1.5fr 1fr"
                : "3.5fr 1.5fr 1.5fr 1.5fr 1fr",
            p: 2,
            textAlign: "center"
          } }
        >
          <Box>{ t("title_training") }</Box>
          <Box sx={ { display: filter === "all" ? "block" : "none" } }>
            { t("type_training") }
          </Box>
          <Box>{ t("dispenser_field") }</Box>
          <Box>{ t("date_start_training") }</Box>
          <Box>{ t("date_end_training") }</Box>
          <Box>{ t("all_status") }</Box>
        </Box>
      ) }

      { /* Dati */ }
      <Box sx={ { mt: 2 } }>
        { paginatedCourses.map((course, idx) => (
          <CommonWidgetContainer
            key={ idx }
            onClick={ () => onSelect(course) }
            sx={ {
              backgroundColor: selectedCourse === course ? theme.customColors.backgroundPrimaryControlHover : "",
              cursor: "pointer",
              display: "grid",
              gap: 1,
              gridTemplateColumns: isXs
                ? "1fr"
                : filter === "all"
                  ? "2fr 1.5fr 1.5fr 1.5fr 1.5fr 1fr"
                  : "3.5fr 1.5fr 1.5fr 1.5fr 1fr",
              mb: 1,
              p: 2,
              transition: "all .2s",
              width: "100%",
              maxWidth: "100%"
            } }
          >
            { isXs ? (
              <>
                <Box>
                  <strong>{ t("title_training") }: </strong> { course.title }
                </Box>
                { filter === "all" && (
                  <Box>
                    <strong>{ t("type_training") }: </strong> { course.type }
                  </Box>
                ) }
                <Box>
                  <strong>{ t("dispenser_field") }: </strong> { course.provider }
                </Box>
                <Box>
                  <strong>{ t("date_start_training") }: </strong>{ " " }
                  { formatDateIt(course.startDate) }
                </Box>
                <Box>
                  <strong>{ t("date_end_training") }: </strong>{ " " }
                  { formatDateIt(course.endDate) }
                </Box>
                <Box>
                  <strong>{ t("all_status") }: </strong>{ " " }
                  <img
                    src={
                      course.status === "APPROVED"
                        ? approvedIcon
                        : course.status === "REJECTED"
                          ? rejectedIcon
                          : pendingIcon
                    }
                    alt={ course.status }
                    width={ 20 }
                    height={ 20 }
                    loading="lazy"
                  />
                </Box>
              </>
            ) : (
              <>
                <Box sx={ { textAlign: "center" } }>{ course.title }</Box>
                { filter === "all" && (
                  <Box sx={ { textAlign: "center" } }>{ course.type }</Box>
                ) }
                <Box sx={ { textAlign: "center" } }>{ course.provider }</Box>
                <Box sx={ { textAlign: "center" } }>
                  { formatDateIt(course.startDate) }
                </Box>
                <Box sx={ { textAlign: "center" } }>
                  { formatDateIt(course.endDate) }
                </Box>
                <Box sx={ { textAlign: "center" } }>
                  <img
                    src={
                      course.status === "APPROVED"
                        ? approvedIcon
                        : course.status === "REJECTED"
                          ? rejectedIcon
                          : pendingIcon
                    }
                    alt={ course.status }
                    width={ 24 }
                    height={ 24 }
                    loading="lazy"
                  />
                </Box>
              </>
            ) }
          </CommonWidgetContainer>
        )) }
      </Box>

      { /* Paginazione */ }
      <Box
        sx={ {
          alignItems: "center",
          display: "flex",
          gap: 0.5,
          justifyContent: isXs ? "center" : "flex-end",
          p: 2,
          mt: "auto",
          bgcolor: theme.linearGradients.gradientB,
          flexDirection: "row",
          flexWrap: "nowrap",
          overflowX: "auto"

        } }
      >
        <Button
          disabled={ currentPage === 1 }
          onClick={ () => setCurrentPage(Math.max(currentPage - 1, 1)) }
        >
          <Icon
            icon="arrow_left_glow"
            color={ theme.customColors.systemPrimary02 }
            size={ 25 }
          />
        </Button>

        { getPaginationItems(currentPage, Math.ceil(courses.length / itemsPerPage)).map((i, index) =>
          i === "..." ? (
            <Box key={ `ellipsis-${index}` } sx={ { px: 1, color: theme.customColors.textPrimary } }>
              ...
            </Box>
          ) : (
            <Button
              key={ i }
              onClick={ () => setCurrentPage(i as number) }
              variant={ currentPage === i as number ? "contained" : "outlined" }
              sx={ { borderRadius: 1, height: 32, minWidth: 30, p: 0 } }
            >
              { i as number }
            </Button>
          )) }

        <Button
          disabled={ currentPage === Math.ceil(courses.length / itemsPerPage) }
          onClick={ () =>
            setCurrentPage(Math.min(currentPage + 1, Math.ceil(courses.length / itemsPerPage)))
          }
        >
          <Icon
            icon="Icons_arrow-right_glow"
            color={ theme.customColors.systemPrimary02 }
            size={ 25 }
          />
        </Button>
      </Box>
    </Box>
  );
};
